import {orderBy, filter, get, map, flatMap, uniqBy, sortBy} from "lodash";
import {isSignDocComplete, SignDocUploadStatus, SignDocTypes, UPLOAD_PROGRESS_REJECTED} from "../../Components/SignDoc/SignDocLib";
import { i18n } from "../i18n/i18n";
import { fetchAllAdminSignDocs } from "../../Components/Hooks/useQueryHooks";


/**
 * Sorts UserSignDocs with priority for non-approved upload documentsז
 * Sorting order:
 * 1. UPLOAD_DOCUMENT with progressPercent === 50 (awaiting approval) - sorted by finishByDate asc
 * 2. All other incomplete SignDocs - sorted by finishByDate asc
 *
 * @param {Array<object>} userSignDocs - Array of UserSignDoc objects to sort
 * @returns {Array<object>} Sorted array of UserSignDoc objects
 */
export const sortUserSignDocsWithApprovalPriority = (userSignDocs) => {
    // Separate into awaiting approval and others
    const rejectedUploadDocuments = [];
    const certifications = [];
    const others = [];

    userSignDocs.forEach(signDoc => {
        const signDocType = get(signDoc, 'signDoc.type');
        const progressPercent = get(signDoc, 'progressPercent');

        // Check if it's an upload document awaiting approval (progress === 50)
        if (signDocType === SignDocTypes.UPLOAD_DOCUMENT && progressPercent === UPLOAD_PROGRESS_REJECTED && get(signDoc, 'additionalData.rejectedBy', null) !== null ) {
            rejectedUploadDocuments.push(signDoc);
        } else if (signDocType === SignDocTypes.CERTIFICATION) {
            certifications.push(signDoc);
        } else {
            others.push(signDoc);
        }
    });

    // Sort each group by finishByDate ascending
    const sortedRejectedUploadDocuments = orderBy(rejectedUploadDocuments, ['finishByDate'], ['asc']);
    const sortedCertifications = orderBy(certifications, ['finishByDate'], ['asc']);
    const sortedOthers = orderBy(others, ['finishByDate'], ['asc']);

    // Combine: rejected upload documents first, then certifications, then others
    return [...sortedRejectedUploadDocuments, ...sortedCertifications, ...sortedOthers];
};

export const getSignDocsToDo = ({userSignDocs, showNonForceCompletion = false}) => {
    const filteredSignDocs = filter(userSignDocs, (signDoc) => {
        if (showNonForceCompletion && get(signDoc, 'signDoc.doNotForceCompletion', false) && get(signDoc, 'signDoc.uploadStatus', SignDocUploadStatus.READY) !== SignDocUploadStatus.FAILED) {
            return true;
        }

        // Use the centralized completion logic
        return !isSignDocComplete({signDoc}) && get(signDoc, 'signDoc.uploadStatus', SignDocUploadStatus.READY) !== SignDocUploadStatus.FAILED;
    });

    // Apply unified sorting with approval priority
    return sortUserSignDocsWithApprovalPriority(filteredSignDocs);
};

export const getCompletedSignDocs = ({userSignDocs}) => {
    return orderBy(filter(userSignDocs, (signDoc) => {
        // Use centralized completion logic AND check for completedOnDate
        return isSignDocComplete({signDoc}) && get(signDoc, 'signDoc.uploadStatus', SignDocUploadStatus.READY) !== SignDocUploadStatus.FAILED;
    }), ['completedOnDate'], ['desc']);
};

/**
 * Dispatches a custom event when a user clicks/enters a SignDoc
 * This allows components like MyOpenTasks to refresh their data immediately
 * and update the state from "not started" to "started"
 *
 * @param {Object} params - The function parameters container object.
 * @param {string} params.signDocId - The ID of the entered SignDoc.
 * @param {string} params.signDocType - The type of the entered SignDoc.
 */
export const dispatchSignDocCardEnterEvent = ({ signDocId, signDocType }) => {
    const event = new CustomEvent('signDocCardEnter', {
        detail: { signDocId, signDocType }
    });
    window.dispatchEvent(event);
};

/**
 * Adds an event listener for SignDoc card enter events
 *
 * @param {Function} callback - The callback function to execute when a SignDoc is entered.
 * @returns {Function} A cleanup function to remove the event listener.
 */
export const addSignDocCardEnterListener = (callback) => {
    const handleSignDocCardEnter = (event) => {
        callback(event.detail);
    };
    
    window.addEventListener('signDocCardEnter', handleSignDocCardEnter);
    
    // Return cleanup function
    return () => {
        window.removeEventListener('signDocCardEnter', handleSignDocCardEnter);
    };
};

export const getOrganizationalUnitsAssignedToSignDocType = async ({signDocTypes, categories, asOptions = false, withAllOption = false}) => {
    // Use centralized fetcher with React Query cache (with built-in filtering)
    const adminSignDocs = await fetchAllAdminSignDocs({types: signDocTypes, categories});

    const organizationalUnits = uniqBy(
        flatMap(adminSignDocs, (signDoc) => {
            const orgSignDocs = get(signDoc, 'organizationSignDoc', []);
            const validOrgSignDocs = filter(orgSignDocs, orgSignDoc => get(orgSignDoc, 'isValid', false));
            return map(validOrgSignDocs, orgSignDoc => get(orgSignDoc, 'organization'));
        }).filter(Boolean), // Remove any null/undefined organizations
        'id'
    );
    if (asOptions) {
        let organizationWithoutVPO = filter(organizationalUnits, (organization) => !get(organization, 'isPersonal'));
        const sortedOrganizationalUnitsOptions = sortBy(map(organizationWithoutVPO, (organization) => ({
            orgLogo: organization.orgLogo,
            text: organization.orgName,
            value: organization.id
        })), 'text')
        if (withAllOption) {
            sortedOrganizationalUnitsOptions.unshift({
                orgLogo: null,
                text: i18n.translate('allOrganizations'),
                value: 'all'
            })
        }
        return sortedOrganizationalUnitsOptions
    }
    return organizationalUnits
};

/**
 * Returns true if the LearningPath SignDoc is configured for unordered/free-path mode.
 * Strict equality: only `additionalData.unorderedLearningPathTasks === true` returns true.
 * Existing SignDocs without the field, or any non-strict-true value, return false.
 *
 * @param {object} signDoc
 * @returns {boolean}
 */
export const isLearningPathUnordered = (signDoc) => {
    return get(signDoc, "additionalData.unorderedLearningPathTasks", false) === true;
};